import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import PageHeader from '../../../components/PageHeader'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import Title from '../../../components/v2/Title'
import {
  CarInformation,
  IndividualPersonalInformation,
  IntTplOfferType,
  LegalEntityPersonalInformation,
} from '../../../domain/types'
import { RootState, useAppSelector } from '../../../store'
import { IntTplState, setOffer } from '../../../store/internationalTplSlice'
import api from '../../../api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import Loader from '../../../components/Loader'
import Message from '../../../components/Message'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { AuthState } from '../../../store/authSlice'
import { extractErrorMessage } from '../../../utils/errorUtils'
import { AppState } from '../../../store/appSlice'
import { useInternationalTplProgress } from './useInternationalTplProgress'
import { useInternationalTplPages } from './useInternationalTplPages'

const InternationalTplOffer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetIntTplPageInfoQuery('international-tpl-offer')

  const formatBirthDate = useCallback((rawDate?: string): string | null => {
    if (!rawDate) return null
    const sanitized = rawDate.replace(/\s/g, '')

    // Check if date is already in ISO format (YYYY-MM-DD)
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (isoDateRegex.test(sanitized)) {
      return sanitized
    }

    // Try to parse DD/MM/YYYY format
    const parts = sanitized.split('/')
    if (parts.length !== 3) return null
    const [day, month, year] = parts
    if (!day || !month || !year) return null
    return `${year}-${month}-${day}`
  }, [])

  const { checkout, filter } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)
  const [getOfferRequest, { isLoading: isOfferLoading, error: offerError }] = api.useGetIntTplOfferMutation()
  const [requestError, setRequestError] = useState<string | null>(null)

  // Use the hook to get pages and page data from Redux
  const { pages: pagesFromHook, isLoading: isPagesLoading } = useInternationalTplPages()
  const { totalCount, passedCount, pages: progressPages } = useInternationalTplProgress()
  const displayPages = progressPages.length > 0 ? progressPages : pagesFromHook
  const requestRef = useRef<{ payloadKey: string | null; isRequesting: boolean }>({
    payloadKey: null,
    isRequesting: false,
  })
  const getOfferRequestRef = useRef(getOfferRequest)

  useEffect(() => {
    getOfferRequestRef.current = getOfferRequest
  }, [getOfferRequest])

  const offerPayload = useMemo(() => {
    const { user, vehicle } = checkout
    if (!user || !vehicle || !filter.startDate || !filter.isoCode || !filter.insurancePeriodId) {
      return null
    }

    if (!user.clientEmail || !user.clientPhone) {
      return null
    }

    if (!user.clientDocTypeId) {
      return null
    }

    const contactInformation = {
      email: user.clientEmail,
      phoneNumber: user.clientPhone,
    }

    const buildUploadedPages = (pages: Array<{ page: number; address?: string | null | undefined }>) =>
      pages.filter((item) => item.address).map((item) => ({ page: item.page, address: item.address as string }))

    const insurerDocuments = buildUploadedPages([
      { page: 1, address: user.clientDocOneSide },
      { page: 2, address: user.clientDocSecondSide },
      { page: 3, address: user.clientDocThirdSide },
    ])

    if (insurerDocuments.length === 0) {
      return null
    }

    const documents = [
      {
        documentTypeId: user.clientDocTypeId,
        uploadedPages: insurerDocuments,
      },
    ]

    const isIndividual = user.clientType === 'individual'

    let individualInformation: IndividualPersonalInformation | null = null
    let legalInformation: LegalEntityPersonalInformation | null = null

    if (isIndividual) {
      const birthDate = formatBirthDate(user.dateOfbirth as never as string)
      // Safely get ISO code - handle both string and number (country ID) cases
      const cityzenshipIso = typeof user.cityzenship === 'string' ? user.cityzenship : undefined
      const isArmenianCitizen = cityzenshipIso?.toLowerCase() === 'hy'
      if (
        !birthDate ||
        !user.clientTypeId ||
        !user.cityzenship ||
        !user.idNumber ||
        !user.gender ||
        !user.translations?.en?.firstName ||
        !user.translations?.en?.lastName ||
        (isArmenianCitizen && !user.socialSecurityNumber)
      ) {
        return null
      }

      individualInformation = {
        clientTypeId: user.clientTypeId,
        citizenshipCountryCode: user.cityzenship,
        socialSecurityNumber: isArmenianCitizen ? user.socialSecurityNumber || '' : '',
        personalNumber: user.idNumber,
        birthDate,
        countryId: user.countryId,
        gender: user.gender,
        firstNameEn: user.translations.en.firstName,
        lastNameEn: user.translations.en.lastName,
        firstNameGe: user.translations?.ge?.firstName || null,
        lastNameGe: user.translations?.ge?.lastName || null,
      }
    } else {
      if (!user.clientTypeId || !user.registrationCountry || !user.companyName) {
        return null
      }

      if (user.clientType === 'legal' && (!user.representativeName || !user.representativeSurname)) {
        return null
      }

      if (user.clientType === 'individual' && (!user.translations?.en?.firstName || !user.translations?.en?.lastName)) {
        return null
      }

      legalInformation = {
        clientTypeId: user.clientTypeId,
        registrationCountryCode: user.registrationCountry,
        identificationNumber: user.identificationNumber || '',
        clientSubtypeId: user.legalEntityType || null,
        companyName: user.companyName,
        countryId: user.registrationCountryId,
        representativeInformation: {
          firstNameEn: user?.representativeName || '',
          lastNameEn: user?.representativeSurname || '',
        },
      }
    }

    if (
      !vehicle.registrationCountry ||
      !vehicle.registrationNumber ||
      !vehicle.usageId ||
      !vehicle.vehicleTypeId ||
      !vehicle.vehicleId ||
      !vehicle.vehicleModelId ||
      !vehicle.manufactureYear ||
      !vehicle.engineCapacity ||
      !vehicle.color ||
      !vehicle.documentTypeId ||
      !vehicle.documentNumber
    ) {
      return null
    }

    const vehicleDocuments = buildUploadedPages([
      { page: 1, address: vehicle.vehicleDocOneSide },
      { page: 2, address: vehicle.vehicleDocSecondSide },
    ])

    const carInformation: CarInformation = {
      registrationCountry: vehicle.registrationCountry,
      registrationNumber: vehicle.registrationNumber,
      vinCode: vehicle.codeType === 'vin' ? vehicle.code : undefined,
      chassisNumber: vehicle.codeType === 'chassisNumber' ? vehicle.code : undefined,
      bodyNumber: vehicle.codeType === 'bodyNumber' ? vehicle.code : undefined,
      usageTypeId: Number(vehicle.usageId),
      vehicleTypeId: Number(vehicle.vehicleTypeId),
      markId: Number(vehicle.vehicleId),
      modelId: Number(vehicle.vehicleModelId),
      manufactureYear: Number(vehicle.manufactureYear),
      engineCapacity: String(vehicle.engineCapacity),
      color: vehicle.color,
      documentNumber: vehicle.documentNumber,
      documents: [
        {
          documentTypeId: vehicle.documentTypeId,
          uploadedPages: vehicleDocuments,
        },
      ],
    }

    const insurerInformation = {
      individualPersonalInformation: individualInformation,
      legalEntityPersonalInformation: legalInformation,
      contactInformation,
      documents,
    }

    return {
      lang: lang,
      startDate: filter.startDate,
      countryCode: filter.isoCode,
      insurancePeriodId: filter.insurancePeriodId,
      isVehicleOwner: true,
      agreedOnTermsAndConditions: checkout.user.consentTerm,
      insurerInformation,
      carOwnerInformation: insurerInformation,
      carInformation,
    } as IntTplOfferType
  }, [checkout, filter, formatBirthDate])

  useEffect(() => {
    if (!offerPayload) {
      setRequestError(null)
      return
    }

    const payloadKey = JSON.stringify(offerPayload)

    if (requestRef.current.payloadKey === payloadKey || requestRef.current.isRequesting) {
      return
    }

    requestRef.current.payloadKey = payloadKey
    requestRef.current.isRequesting = true

    const fetchOffer = async () => {
      setRequestError(null)
      try {
        const data = await getOfferRequestRef.current(offerPayload).unwrap()
        if (data) {
          dispatch(setOffer(data))
        }
      } catch (error: any) {
        const errorMessage = extractErrorMessage(error, t('anErrorOccurred'), {
          'Contract session expired': t('serviceTemporarilyUnavailable'),
        })
        setRequestError(errorMessage)
        toast.error(errorMessage)
        requestRef.current.payloadKey = null
      } finally {
        requestRef.current.isRequesting = false
      }
    }

    fetchOffer()
  }, [offerPayload, dispatch, t])

  useEffect(() => {
    const currentRef = requestRef.current
    return () => {
      currentRef.payloadKey = null
      currentRef.isRequesting = false
    }
  }, [])

  // auto redirect first page
  useEffect(() => {
    if (!filter?.isoCode) {
      navigate('/international-tpl-country')
    }
    return () => {}
  }, [filter?.isoCode, navigate])

  if (isOfferLoading || isPageInfoLoading || isPagesLoading) {
    return <Loader />
  }

  if (requestError || offerError) {
    const errorMessage =
      requestError ||
      extractErrorMessage(offerError, t('anErrorOccurred'), {
        'Contract session expired': t('serviceTemporarilyUnavailable'),
      })
    return (
      <Wrapper>
        <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
        <Container>
          <Content>
            <Title text={pageInfo?.translations?.[lang]?.title} />
            <Block>
              <Message variant="light-red" text={errorMessage} />
              <Buttons>
                <NavigationButton
                  text={`${t('back')}`}
                  buttonType="prev"
                  onClick={() => {
                    if (auth && auth.user && auth.token && !auth.userLoading) {
                      navigate('/international-tpl-car-info')
                    } else {
                      navigate('/international-tpl-user')
                    }
                  }}
                />
              </Buttons>
            </Block>
          </Content>
        </Container>
      </Wrapper>
    )
  }

  if (!checkout?.offer || Object.keys(checkout.offer).length === 0) {
    return (
      <Wrapper>
        <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
        <Container>
          <Content>
            <Title text={pageInfo?.translations?.[lang]?.title} />
            <Block>
              <Loader />
            </Block>
          </Content>
        </Container>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
      <Container>
        <Content>
          <Title text={pageInfo?.translations?.[lang]?.title} />
          <Block>
            <img style={{ height: '25px', marginRight: 'auto' }} src={checkout?.offer?.iconUrl} alt="insurer-icon" />
            <HR />

            <InlineSection>
              <LeftText>{t('teritorialCoverage')}</LeftText>
              <RigthText>{checkout?.offer?.territorialCoverage}</RigthText>
            </InlineSection>

            <InlineSection>
              <LeftText>{t('vehicleUsage')}</LeftText>
              <RigthText>{checkout?.offer?.vehicleUsage}</RigthText>
            </InlineSection>

            <HR />

            <InlineSection>
              <LeftText>{t('policyDuration')}</LeftText>
              <RigthText>
                {checkout?.offer?.policyDuration} {t('day') as string}
              </RigthText>
            </InlineSection>

            <InlineSection>
              <LeftText>{t('startDate') as string}</LeftText>
              <RigthText>{dayjs(checkout?.offer?.startDate).format('D MMMM YYYY')}</RigthText>
            </InlineSection>

            <HR />

            <InlineSection>
              <LeftText>
                <PriceText>{checkout?.offer?.premium?.toFixed(2)} ₾</PriceText>
              </LeftText>
              <RigthText>
                <ATagStyle href={checkout.offer.policyTermsUrl} target="_blank">
                  <div>{t('insurance_Conditions')}</div>
                </ATagStyle>
              </RigthText>
            </InlineSection>
          </Block>
          <Buttons>
            <NavigationButton
              text={`${t('back')}`}
              buttonType="prev"
              onClick={() => {
                if (auth && auth.user && auth.token && !auth.userLoading) {
                  navigate('/international-tpl-car-info')
                } else {
                  navigate('/international-tpl-user')
                }
              }}
            />
            <NavigationButton
              text={`${t('next')}`}
              buttonType="next"
              onClick={() => navigate('/international-tpl-check-info')}
            />
          </Buttons>
        </Content>
      </Container>
    </Wrapper>
  )
}

const ATagStyle = styled.a`
  font-size: 14px;
  line-height: 10px;
  color: ${Colors.primaryBlack};
  text-align: right;
  div {
    line-height: 18px;
    &:last-child {
      margin-left: 5px;
      display: flex;
      justify-content: center;
      align-items: center;
      svg {
        width: 16px;
        height: 10px;
      }
    }
  }
  /*  */
  &:hover {
    color: ${Colors.primaryBlack};
    div {
      svg {
        color: ${Colors.primaryRed};
      }
    }
  }
`

const PriceText = styled.span`
  font-size: 28px;
  color: ${Colors.primaryBlack};
  text-decoration: none;
  font-weight: 700;
  letter-spacing: 2px;
`

const RigthText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: ${Colors.primaryBlack};
`
const LeftText = styled.div`
  font-size: 14px;
  color: ${Colors.primaryBlack};
`

const InlineSection = styled.div`
  color: ${Colors.nevadaGrey};
  margin-block: 7px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${Colors.primaryBlack};
  font-size: 14px;
  width: 100%;
`

const HR = styled.hr`
  width: 100%;
`

const Content = styled.div`
  margin-top: 40px;
  margin-bottom: 32px;
  z-index: 2;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`
const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  /* gap: 20px; */
  max-width: 432px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  background-color: ${Colors.white};
  border-radius: 8px;
  padding: 20px 32px;
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`

export default InternationalTplOffer
